import { createFileRoute, useRouter } from '@tanstack/react-router'
import { ConfirmDialog, FullView, FullViewContent } from '../../../../components/Modal'
import { ActionButton } from '../../../../components/Button'
import { ChevronBackward, Edit, Delete } from '@nine-thirty-five/material-symbols-react/rounded'
import {
  describeTaskRecurrence,
  useGetTask,
  useRemoveTask,
  useUpdateTask,
  type Task,
  useProjectsStore,
  selectProjectById,
  type TaskFilterView,
} from '@zendia/frontend-shared'
import { DateTime } from 'luxon'
import { useMemo, useState } from 'react'
import { axiosRequest } from '../../../../utilities/axiosClient'
import { TaskView } from '../../../../components/Task/TaskView'
import {
  parseTasksPanelTab,
  type TasksPanelTab,
} from '../../../../components/dashboard/tasksPanelTab'

export const Route = createFileRoute('/_authenticated/tasks/$taskId/detail')({
  component: RouteComponent,
  validateSearch: (
    search,
  ): {
    view: TaskFilterView
    dashboardTab?: TasksPanelTab
    fromAllTasks?: boolean
    goalId?: string
    milestoneId?: string
  } => ({
    view: search.view as TaskFilterView,
    // Which dashboard Tasks panel group the user was on before opening this task, so
    // going back (or deleting it) returns the dashboard to that same group.
    dashboardTab: parseTasksPanelTab(search.dashboardTab),
    // Set when the task was opened from the All Tasks page, so going back returns there
    fromAllTasks: search.fromAllTasks === true || undefined,
    // Set when the task was opened from a goal's Your Plan, so going back returns there, on the
    // same milestone
    goalId: search.goalId as string | undefined,
    milestoneId: search.milestoneId as string | undefined,
  }),
})

function RouteComponent() {
  const { taskId } = Route.useParams()
  const router = useRouter()
  const { view, dashboardTab, fromAllTasks, goalId, milestoneId } = Route.useSearch()
  const { mutateAsync: removeTask, isPending: isDeleting } = useRemoveTask(axiosRequest)
  const { mutateAsync: updateTask } = useUpdateTask(axiosRequest)
  // Shown at once from the list it was opened from, while its own request is in flight
  const { data: task, isLoading: tasksLoading } = useGetTask(axiosRequest, taskId)
  const [isTogglingComplete, setIsTogglingComplete] = useState(false)
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)

  const project = useProjectsStore((state) =>
    task?.projectId ? selectProjectById(state, task.projectId) : undefined,
  )

  // The dashboard's Tasks panel is the main way into this page, so back
  // returns there rather than to the standalone Tasks page, restoring the
  // group the user came from when one was passed along. Opened from a
  // goal's Your Plan or the All Tasks page, it returns there instead.
  const onBack = () => {
    if (fromAllTasks) {
      router.navigate({ to: '/tasks', search: { dashboardTab }, replace: true })
    } else if (goalId) {
      router.navigate({
        to: '/goals/$goalId',
        params: { goalId },
        search: { tab: 'plan', planMilestone: milestoneId },
        replace: true,
      })
    } else {
      router.navigate({ to: '/dashboard', search: { tab: dashboardTab }, replace: true })
    }
  }

  const handleEdit = () => {
    router.navigate({
      to: '/tasks/$taskId/edit',
      params: { taskId },
      search: { view, dashboardTab, fromAllTasks, goalId, milestoneId },
    })
  }

  const handleDelete = async () => {
    try {
      await removeTask(taskId)
      setIsConfirmingDelete(false)
      onBack()
    } catch (error) {
      console.error('Error deleting task:', error)
    }
  }

  const handleToggleComplete = async () => {
    if (!task || isTogglingComplete) return
    setIsTogglingComplete(true)
    try {
      await updateTask({
        taskId: task.id,
        data: {
          completedDate: task.completedDate ? null : DateTime.now().toISODate(),
        } as Partial<Task>,
      })
    } catch (error) {
      console.error('Error toggling task completion:', error)
    } finally {
      setIsTogglingComplete(false)
    }
  }

  const recurrenceText = useMemo(
    () => describeTaskRecurrence({ when: task?.when, recurrence: task?.recurrence }),
    [task?.recurrence, task?.when],
  )

  return (
    <>
      <FullView
        leftAction={
          <ActionButton
            onClick={onBack}
            label="Back"
            icon={<ChevronBackward className="size-6" />}
          />
        }
        centerContent={!task ? <div>Task Details</div> : undefined}
        rightAction={
          <>
            <ActionButton
              onClick={handleEdit}
              label="Edit task"
              icon={<Edit className="size-5" />}
            />
            <ActionButton
              negative
              onClick={() => setIsConfirmingDelete(true)}
              label="Delete task"
              icon={<Delete className="size-5" />}
            />
          </>
        }
      >
        <FullViewContent>
          {tasksLoading ? (
            <div className="h-full flex items-center justify-center">
              <p className="text-neutral-400">Loading task...</p>
            </div>
          ) : !task ? (
            <div className="h-full flex items-center justify-center">
              <p className="text-neutral-400">Task not found</p>
            </div>
          ) : (
            <TaskView
              task={task}
              project={project}
              recurrenceText={recurrenceText}
              onToggleComplete={handleToggleComplete}
              isTogglingComplete={isTogglingComplete}
            />
          )}
        </FullViewContent>
      </FullView>

      <ConfirmDialog
        open={isConfirmingDelete}
        negative
        title="Delete task"
        message={
          task
            ? `Are you sure you want to delete "${task.title}"? This cannot be undone.`
            : 'Are you sure you want to delete this task? This cannot be undone.'
        }
        confirmLabel="Delete"
        isPending={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setIsConfirmingDelete(false)}
      />
    </>
  )
}
